import React, { useState } from 'react';
import { CalendarClock, Check, Sprout, Search } from 'lucide-react';

const C = { forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', soil: '#7A6B57', leaf: '#6B9F4A', gold: '#C9A85B', lime: '#B4D330', sky: '#8FB4D6' };

const CLASS_ORDER = ['early', 'early-main', 'main'];
const CLASS_TONE = {
  early: { bg: 'rgba(107,159,74,0.15)', fg: '#3f6a23' },
  'early-main': { bg: 'rgba(143,180,214,0.2)', fg: '#3a5f7d' },
  main: { bg: 'rgba(201,168,91,0.2)', fg: '#8a6d2b' },
};

const fmtDate = (d) => d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });

export function VarietyPicker({ varieties, selected, onSelect }) {
  const [cls, setCls] = useState('all');
  const [query, setQuery] = useState('');
  if (!varieties || varieties.length === 0) return null;
  const q = query.trim().toLowerCase();

  const presentClasses = CLASS_ORDER.filter((k) => varieties.some((v) => v.harvest_class === k));
  const filtered = varieties
    .filter((v) => cls === 'all' || v.harvest_class === cls)
    .filter((v) => !q || (v.variety_name || '').toLowerCase().includes(q))
    .slice()
    .sort((a, b) => (a.days_to_maturity || 0) - (b.days_to_maturity || 0));
  const sel = varieties.find((v) => v.variety_id === selected) || null;

  const estDate = sel ? fmtDate(new Date(Date.now() + (sel.days_to_maturity || 0) * 86400000)) : null;

  return (
    <div className="hg-glass hg-float rounded-3xl p-6" data-testid="variety-picker">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.16em', color: C.leaf }}>Choose your variety</p>
      </div>
      <p className="text-sm mb-4" style={{ color: C.soil }}>Each variety sets its own harvest timing</p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="variety-class-filter">
        {['all', ...presentClasses].map((k) => (
          <button key={k} onClick={() => setCls(k)} data-testid={`variety-class-${k}`}
            className="px-3.5 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors"
            style={cls === k ? { background: C.forest, color: C.cream, borderColor: C.forest } : { background: '#fff', color: C.forest, borderColor: 'rgba(26,67,49,0.15)' }}>
            {k === 'all' ? 'All' : (CLASS_TONE[k] ? k === 'early-main' ? 'Early-main' : k.charAt(0).toUpperCase() + k.slice(1) : k)}
          </button>
        ))}
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: C.soil }} />
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search varieties…" data-testid="variety-search"
          className="w-full rounded-full border-2 pl-10 pr-4 py-2 text-sm outline-none transition-colors"
          style={{ borderColor: 'rgba(26,67,49,0.15)', color: C.forest, background: '#fff' }} />
      </div>

      <div className="space-y-2" data-testid="variety-list">
        {filtered.length === 0 && (
          <p className="text-sm text-center py-4" style={{ color: C.soil }} data-testid="variety-empty">No varieties match “{query}”</p>
        )}
        {filtered.map((v) => {
          const on = v.variety_id === selected;
          const tone = CLASS_TONE[v.harvest_class] || CLASS_TONE.main;
          return (
            <button key={v.variety_id} onClick={() => onSelect(on ? null : v)} data-testid={`variety-row-${v.variety_id}`}
              className="w-full text-left flex items-center justify-between gap-3 rounded-2xl px-4 py-3 border transition-all"
              style={on ? { background: '#FBFDF4', borderColor: C.forest, boxShadow: '0 0 0 3px rgba(180,211,48,0.35)' } : { background: '#fff', borderColor: 'rgba(26,67,49,0.08)' }}>
              <div className="min-w-0">
                <p className="font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{v.variety_name}</p>
                <p className="text-xs truncate" style={{ color: C.soil }}>{v.maturity_label} · {v.texture}{v.best_for?.length ? ` · ${v.best_for.slice(0, 2).join(', ')}` : ''}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full" style={{ letterSpacing: '0.04em', background: tone.bg, color: tone.fg }}>{v.harvest_class_label}</span>
                {on && <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: C.leaf }}><Check className="w-3.5 h-3.5 text-white" /></span>}
              </div>
            </button>
          );
        })}
      </div>

      {sel && (
        <div className="mt-4 rounded-2xl overflow-hidden border" style={{ borderColor: 'rgba(26,67,49,0.1)' }} data-testid="variety-detail">
          <div className="relative overflow-hidden p-5" style={{ background: `linear-gradient(135deg, ${C.forest}, ${C.forestDeep})`, color: C.cream }}>
            <div className="absolute -top-12 -right-8 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'rgba(180,211,48,0.18)' }} />
            <div className="relative">
              <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.16em', color: C.lime }}>Your variety</p>
              <h3 className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="variety-detail-name">{sel.variety_name}</h3>
              <span className="inline-block mt-2 text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ background: 'rgba(250,246,236,0.15)' }}>{sel.harvest_class_label}</span>
            </div>
          </div>
          <div className="p-5" style={{ background: C.cream }}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-4">
              {[['Days to maturity', sel.maturity_label], ['Texture', sel.texture], ['Best for', (sel.best_for || []).join(' · ')], ['Skin / flesh', [sel.skin_colour, sel.flesh_colour].filter(Boolean).join(' / ')]]
                .filter(([, val]) => val).map(([k, val]) => (
                <div key={k}>
                  <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.04em', color: C.soil }}>{k}</p>
                  <p className="font-bold" style={{ color: C.forest }}>{val}</p>
                </div>
              ))}
            </div>
            {sel.note && <p className="text-sm italic mb-4" style={{ color: C.soil, borderLeft: `2px solid #8EAA97`, paddingLeft: 12 }}>{sel.note}</p>}
            <div className="relative overflow-hidden rounded-2xl p-4" style={{ background: `linear-gradient(135deg, ${C.forest}, ${C.forestDeep})`, color: C.cream }} data-testid="variety-estimate">
              <div className="absolute -top-8 -right-5 w-28 h-28 rounded-full pointer-events-none" style={{ background: 'rgba(180,211,48,0.18)' }} />
              <div className="relative flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(180,211,48,0.2)' }}><Sprout className="w-5 h-5" style={{ color: C.lime }} /></div>
                <div>
                  <p className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}><CalendarClock className="w-4 h-4 inline mr-1" />Approx. harvest · {estDate}</p>
                  <p className="text-xs" style={{ color: 'rgba(250,246,236,0.75)' }}>If sown today — {sel.days_to_maturity} days maturity. Plant it to lock this into your harvest predictor.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
